import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

interface TourDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

// Tour images are numbered Step1.png through Step10.png
// const TOUR_SLIDES = Array.from({ length: 10 }, (_, i) => ({
//   image: `/tour/Step${i + 1}.png`,
//   title: `Welcome to Trackplicant - Step ${i + 1}`,
//   description: `This is slide ${i + 1}. Learn how to make the most of Trackplicant's features to land your dream job.`,
// }));

const TOUR_SLIDES = [
  {
    image: `/tour/Step0.png`,
    title: `Welcome to Trackplicant 🎉`,
    description: `Let's take a quick tour to show you how to save jobs, stay organized, and get the most out of Trackplicant. It takes about 30 seconds, and you can skip at any time.`,
  },
  {
    image: `/tour/Step1.png`,
    title: `Your Feed`,
    description: `See updates, follow-ups, and progress across your job search - all in one place.`,
  },
  {
    image: `/tour/Step2.png`,
    title: `AI-Powered Job Suggestions`,
    description: `Discover job matchs tailored to your experience and goals.`,
  },
  {
    image: `/tour/Step3.png`,
    title: `Track a Job`,
    description: `Save jobs you're interested in by clicking "Save Job" - they'll appear in your Saved Jobs so you can manage follow-ups and progress.`,
  },
  {
    image: `/tour/Step4.png`,
    title: `Your Saved Jobs`,
    description: `All the jobs you track appear here - your central place to see what you're working on. `,
  },
  {
    image: `/tour/Step5.png`,
    title: `Add a Saved Job`,
    description: `Use this button to add a job you've applied for or want to track — we'll walk through it next.`,
  },
  {
    image: `/tour/Step6.png`,
    title: `Paste a Job URL`,
    description: `Paste a job posting link and we'll extract the details automatically. You can also email links to alerts@trackplicant.com and we'll add them for you.`,
  },
  {
    image: `/tour/Step7.png`,
    title: `View Saved Job Details`,
    description: `Click the saved job card to open the saved job’s full details, notes, and AI tools to manage your progress.`,
  },
  {
    image: `/tour/Step8.png`,
    title: `Saved Job Details`,
    description: `See your match score, job details, contacts, and AI tools for resumes, cover letters, and follow-ups — all in one place.`,
  },
  {
    image: `/tour/Step9.png`,
    title: `Your Profile & Settings`,
    description: `Manage your resume, job preferences, and account settings — all in one place.`,
  },
  {
    image: `/tour/Step10.png`,
    title: `You're all Set! 🚀`,
    description: `Enjoy your 2-week Pro trial — explore everything Trackplicant can do.

If you uploaded a resume, check back in a few hours for Suggested Jobs. 

You can restart this tour anytime from the Tour button in the upper right corner.`,
  },
];

export function TourDialog({
  open,
  onOpenChange,
  onComplete,
}: TourDialogProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleNext = () => {
    if (currentSlide < TOUR_SLIDES.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      // Last slide - complete the tour
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setCurrentSlide(0); // Reset for next time
    onOpenChange(false);
    onComplete?.();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // User closed the dialog (via X, ESC, or clicking outside)
      // Treat this as skipping the tour
      handleComplete();
    } else {
      onOpenChange(isOpen);
    }
  };

  const slide = TOUR_SLIDES[currentSlide];
  const isLastSlide = currentSlide === TOUR_SLIDES.length - 1;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className='!w-[95vw] !max-w-[95vw] !h-[95vh] !max-h-[95vh] sm:!max-w-[95vw] md:!max-w-[95vw] lg:!max-w-[95vw] flex flex-col p-6'
        hasCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className='text-2xl'>{slide.title}</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-6 flex-1 overflow-hidden'>
          {/* Tour Image - takes up most of the space */}
          <div className='relative w-full flex-1 flex items-center justify-center overflow-hidden  rounded-lg'>
            <img
              src={slide.image}
              alt={slide.title}
              className='max-w-full max-h-full object-contain border-4 border-blue-700'
            />
          </div>

          {/* Slide Description */}
          <div className='text-center px-4 py-2 min-h-[60px]'>
            <p className='text-muted-foreground text-lg whitespace-pre-line'>
              {slide.description}
            </p>
          </div>

          {/* Progress Indicator */}
          <div className='flex justify-center gap-2'>
            {TOUR_SLIDES.map((_, index) => (
              <div
                key={index}
                className={`h-2.5 w-2.5 rounded-full transition-colors ${
                  index === currentSlide
                    ? 'bg-primary'
                    : index < currentSlide
                      ? 'bg-primary/50'
                      : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>

        <DialogFooter className='flex justify-between sm:justify-between'>
          <Button variant='ghost' onClick={handleSkip} size='lg'>
            Skip
          </Button>
          <div className='flex gap-3 items-center'>
            <span className='text-base text-muted-foreground'>
              {currentSlide + 1} / {TOUR_SLIDES.length}
            </span>
            <Button onClick={handleNext} size='lg'>
              {isLastSlide ? 'Get Started' : 'Next'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
